import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';

import { TrustedDestinationsService } from '../../../core/services/trusted-destinations.service';
import * as TrustedDestinationsActions from './trusted-destinations.actions';
import {
  selectTrustedDestinationsMeta,
  selectTrustedDestinationsLastQuery,
} from './trusted-destinations.selectors';
import {
  DEFAULT_TRUSTED_DESTINATIONS_CURRENT_PAGE,
  DEFAULT_TRUSTED_DESTINATIONS_PER_PAGE,
} from '../models/trusted-destination.model';

@Injectable()
export class TrustedDestinationsEffects {
  private actions$ = inject(Actions);
  private service = inject(TrustedDestinationsService);
  private store = inject(Store);

  loadTrustedDestinations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrustedDestinationsActions.loadTrustedDestinations),
      switchMap((action) =>
        this.service
          .getList({
            page: action.page ?? DEFAULT_TRUSTED_DESTINATIONS_CURRENT_PAGE,
            per_page: action.perPage ?? DEFAULT_TRUSTED_DESTINATIONS_PER_PAGE,
            search: action.search ?? '',
            sort: action.sort ?? 'created_at',
            sort_direction: action.sortDirection ?? 'desc',
            filters: action.filters,
            filter_link: action.filterLink,
          })
          .pipe(
            map((response) =>
              TrustedDestinationsActions.loadTrustedDestinationsSuccess({
                items: response.data,
                meta: response.meta,
              })
            ),
            catchError((error) =>
              of(
                TrustedDestinationsActions.loadTrustedDestinationsFailure({
                  error: error?.message ?? 'Failed to load trusted destinations',
                })
              )
            )
          )
      )
    )
  );

  loadTrustedDestination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrustedDestinationsActions.loadTrustedDestination),
      switchMap((action) =>
        this.service.getById(action.uuid).pipe(
          map((item) =>
            TrustedDestinationsActions.loadTrustedDestinationSuccess({ item })
          ),
          catchError((error) =>
            of(
              TrustedDestinationsActions.loadTrustedDestinationFailure({
                error: error?.message ?? 'Failed to load trusted destination',
              })
            )
          )
        )
      )
    )
  );

  createTrustedDestination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrustedDestinationsActions.createTrustedDestination),
      switchMap((action) =>
        this.service.create(action.payload).pipe(
          map(() => TrustedDestinationsActions.createTrustedDestinationSuccess()),
          catchError((error) =>
            of(
              TrustedDestinationsActions.createTrustedDestinationFailure({
                error: error?.message ?? 'Failed to create trusted destination',
              })
            )
          )
        )
      )
    )
  );

  updateTrustedDestination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrustedDestinationsActions.updateTrustedDestination),
      switchMap((action) =>
        this.service.update(action.uuid, action.payload).pipe(
          map(() => TrustedDestinationsActions.updateTrustedDestinationSuccess()),
          catchError((error) =>
            of(
              TrustedDestinationsActions.updateTrustedDestinationFailure({
                error: error?.message ?? 'Failed to update trusted destination',
              })
            )
          )
        )
      )
    )
  );

  deleteTrustedDestination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrustedDestinationsActions.deleteTrustedDestination),
      switchMap((action) =>
        this.service.delete(action.uuid).pipe(
          map(() => TrustedDestinationsActions.deleteTrustedDestinationSuccess()),
          catchError((error) =>
            of(
              TrustedDestinationsActions.deleteTrustedDestinationFailure({
                error: error?.message ?? 'Failed to delete trusted destination',
              })
            )
          )
        )
      )
    )
  );

  exportTrustedDestinations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrustedDestinationsActions.exportTrustedDestinations),
      switchMap((action) =>
        this.service
          .export({
            selected_rows: action.selectedRows,
            search: action.search,
            filters: action.filters,
            filter_link: action.filterLink,
            sort: action.sort,
            sort_direction: action.sortDirection,
          })
          .pipe(
            map(() => TrustedDestinationsActions.exportTrustedDestinationsSuccess()),
            catchError((error) =>
              of(
                TrustedDestinationsActions.exportTrustedDestinationsFailure({
                  error: error?.message ?? 'Failed to export trusted destinations',
                })
              )
            )
          )
      )
    )
  );

  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TrustedDestinationsActions.createTrustedDestinationSuccess,
        TrustedDestinationsActions.updateTrustedDestinationSuccess,
        TrustedDestinationsActions.deleteTrustedDestinationSuccess
      ),
      withLatestFrom(
        this.store.select(selectTrustedDestinationsMeta),
        this.store.select(selectTrustedDestinationsLastQuery)
      ),
      map(([, meta, lastQuery]) =>
        TrustedDestinationsActions.loadTrustedDestinations({
          page: meta?.currentPage ?? DEFAULT_TRUSTED_DESTINATIONS_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_TRUSTED_DESTINATIONS_PER_PAGE,
          search: lastQuery.search,
          filters: lastQuery.filters,
          filterLink: lastQuery.filterLink,
          sort: lastQuery.sort,
          sortDirection: lastQuery.sortDirection,
        })
      )
    )
  );
}
